/**
 * Transient notifications. Spec 22.3.
 *
 * `lib/errors.ts` has mapped ten error codes to `{ action: 'toast' }` and `RATE_LIMITED` to
 * `{ action: 'toast-with-countdown' }` since phase 1, and nothing has ever rendered one —
 * the behaviour table described a UI that did not exist. This is that UI.
 *
 * One live region for the whole application, declared once and never remounted. A region
 * created at the same moment as the message it contains is frequently not announced at
 * all: assistive technology watches an existing region for changes, so the container has to
 * be there first.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

export type ToastTone = 'info' | 'warning' | 'error' | 'success';

export type Toast = {
  readonly id: string;
  readonly tone: ToastTone;
  readonly message: string;
  /** Seconds remaining, for spec 22.3's `toast-with-countdown` (RATE_LIMITED). */
  readonly countdownSeconds?: number;
};

export type ToastInput = Omit<Toast, 'id'>;

type ToastContextValue = {
  readonly show: (toast: ToastInput) => void;
  readonly dismiss: (id: string) => void;
  readonly toasts: readonly Toast[];
};

const ToastContext = createContext<ToastContextValue | null>(null);

/** How long a toast stays before dismissing itself. A countdown toast manages its own life. */
const DEFAULT_TTL_MS = 6000;

export function ToastProvider({ children }: { readonly children: ReactNode }): ReactNode {
  const [toasts, setToasts] = useState<readonly Toast[]>([]);
  /* Timers are cleared on unmount so a dismissed route cannot set state after teardown. */
  const timers = useRef(new Map<string, ReturnType<typeof setTimeout>>());

  const dismiss = useCallback((id: string) => {
    const timer = timers.current.get(id);
    if (timer !== undefined) {
      clearTimeout(timer);
      timers.current.delete(id);
    }
    setToasts((current) => current.filter((toast) => toast.id !== id));
  }, []);

  const show = useCallback(
    (input: ToastInput) => {
      const id = crypto.randomUUID();
      setToasts((current) => [...current, { ...input, id }]);

      const ttl =
        input.countdownSeconds === undefined ? DEFAULT_TTL_MS : input.countdownSeconds * 1000;
      timers.current.set(
        id,
        setTimeout(() => {
          dismiss(id);
        }, ttl),
      );
    },
    [dismiss],
  );

  useEffect(() => {
    const pending = timers.current;
    return () => {
      for (const timer of pending.values()) clearTimeout(timer);
      pending.clear();
    };
  }, []);

  const value = useMemo(() => ({ show, dismiss, toasts }), [show, dismiss, toasts]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <ToastHost />
    </ToastContext.Provider>
  );
}

export function useToast(): ToastContextValue {
  const context = useContext(ToastContext);
  if (context === null) {
    throw new Error('useToast must be used inside a ToastProvider');
  }
  return context;
}

const TONES: Record<ToastTone, string> = {
  info: 'border-border bg-surface-raised text-ink',
  warning: 'border-accent/40 bg-surface-raised text-ink',
  error: 'border-danger/40 bg-surface-raised text-danger',
  success: 'border-success/40 bg-surface-raised text-success',
};

function ToastHost(): ReactNode {
  const { toasts, dismiss } = useToast();

  return (
    /*
     * `pointer-events-none` on the stack and `auto` on each toast: the region sits over the
     * page, and a toast in the corner must not swallow clicks meant for what is underneath.
     */
    <div
      className="pointer-events-none fixed inset-x-0 bottom-0 z-50 flex flex-col items-center
        gap-2 p-4 sm:items-end"
    >
      {/*
       * Two regions rather than one. An error interrupts; anything else waits for a pause.
       * A single region would have to pick one politeness for both, and spec 4.8 reserves
       * `assertive` for state the user must act on.
       */}
      <div aria-live="assertive" role="alert" className="contents">
        {toasts
          .filter((toast) => toast.tone === 'error')
          .map((toast) => (
            <ToastCard key={toast.id} toast={toast} onDismiss={dismiss} />
          ))}
      </div>

      <div aria-live="polite" role="status" className="contents">
        {toasts
          .filter((toast) => toast.tone !== 'error')
          .map((toast) => (
            <ToastCard key={toast.id} toast={toast} onDismiss={dismiss} />
          ))}
      </div>
    </div>
  );
}

function ToastCard({
  toast,
  onDismiss,
}: {
  readonly toast: Toast;
  readonly onDismiss: (id: string) => void;
}): ReactNode {
  return (
    <div
      className={`pointer-events-auto flex w-full max-w-sm items-start gap-3 rounded-md border
        px-3 py-2 text-sm shadow-sm ${TONES[toast.tone]}`}
    >
      <span className="flex-1">{toast.message}</span>
      {toast.countdownSeconds !== undefined && <Countdown seconds={toast.countdownSeconds} />}
      <button
        type="button"
        onClick={() => {
          onDismiss(toast.id);
        }}
        /*
         * A real accessible name. An `×` glyph alone is announced as "times" or skipped
         * entirely, which leaves a keyboard user with a control they cannot identify.
         */
        aria-label="Dismiss"
        className="text-ink-muted hover:text-ink"
      >
        ×
      </button>
    </div>
  );
}

/**
 * Spec 22.3's countdown, for `RATE_LIMITED`.
 *
 * `aria-hidden`: the number changes every second, and a live region that re-announced it
 * would talk over everything else on the page. The message beside it already says what is
 * happening.
 */
function Countdown({ seconds }: { readonly seconds: number }): ReactNode {
  const [remaining, setRemaining] = useState(seconds);

  useEffect(() => {
    setRemaining(seconds);
    const interval = setInterval(() => {
      setRemaining((value) => (value <= 1 ? 0 : value - 1));
    }, 1000);
    return () => {
      clearInterval(interval);
    };
  }, [seconds]);

  return (
    <span aria-hidden className="tabular-nums text-ink-muted">
      {remaining}s
    </span>
  );
}
